import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Modal,
  Text,
  Link,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import Button from 'components/Button';
import { FormikSelect, FormikTextArea } from 'components/Formik';
import { routePaths } from 'global/routePaths';
import { useCancelPlan } from 'hooks/accountBilling/useCancelPlan';

import { accountCancellationValidationSchema, initialAccountCancellationData } from './schema';

interface IAccountCancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountCancellationModal: React.FC<IAccountCancellationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['accountBilling', 'common']);

  const { mutateAsync, status } = useCancelPlan();

  const feedbackOptions = useMemo(
    () => [
      {
        label: 'Switching to a different app',
        value: 'Switching to a different app',
        reasonLabel: 'Which app, and why?',
      },
      { label: 'Business closed/no more employees', value: 'Business closed/no more employees' },
      {
        label: 'Seasonal/Temporary',
        value: 'Seasonal/Temporary',
        reasonLabel: 'When can we expect you back?',
      },
      {
        label: 'Technical Issues',
        value: 'Technical Issues',
        reasonLabel: 'Please provide feedback',
      },
      {
        label: 'Pricing',
        value: 'Pricing',
        reasonLabel: 'Please provide feedback',
      },
      { label: 'Employee pushback', value: 'Employee pushback' },
      { label: 'Other', value: 'Other', reasonLabel: 'Please describe' },
    ],
    []
  );

  const handleFormSubmit = (values: typeof initialAccountCancellationData) => {
    const postData = { feedback: values.feedback?.value + '. ' + values.reason };
    mutateAsync(postData).then(() => {
      onClose();
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('accountBilling:cancelPlan')}</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialAccountCancellationData}
          validationSchema={accountCancellationValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate>
              <ModalBody mb={4}>
                <FormikSelect
                  options={feedbackOptions}
                  name="feedback"
                  label="Select your reason for cancelling"
                  selectProps={{ isClearable: false }}
                  formControlProps={{ isRequired: true }}
                />

                {values.feedback?.value === 'Pricing' ? (
                  <Text p="2" background="primary.50" borderRadius="md" mb="4">
                    You can get one month free by switching to an annual plan. <br />
                    <Text
                      as={RouterLink}
                      to={routePaths.accountBilling.upgrade}
                      color="secondary.500"
                      fontWeight="semibold"
                    >
                      Would you like to switch?
                    </Text>{' '}
                  </Text>
                ) : null}

                {values.feedback?.reasonLabel ? (
                  <FormikTextArea
                    name="reason"
                    label={values.feedback?.reasonLabel}
                    formControlProps={{ isRequired: true }}
                  />
                ) : null}

                {values.feedback?.value === 'Employee pushback' ? (
                  <Text p="3" background="primary.50" borderRadius="md">
                    Did you implement a{' '}
                    <Link
                      href="https://21677104.fs1.hubspotusercontent-na1.net/hubfs/21677104/Gated%20Resources/Employee%20GPS%20Tracking%20Policy.pdf"
                      isExternal
                      color="secondary.500"
                      fontWeight="semibold"
                    >
                      company tracking policy?
                    </Link>{' '}
                    We have a template you can use to communicate clear app usage expectations with your team. Try
                    implementing one now to be successful with Timeero! Need some help? Watch this expert video on{' '}
                    <Link
                      href="https://fast.wistia.com/embed/channel/kaqi5tskzf?wchannelid=kaqi5tskzf&wmediaid=843vox16zt"
                      isExternal
                      color="secondary.500"
                      fontWeight="semibold"
                    >
                      How to Introduce your Team to Timeero.
                    </Link>
                  </Text>
                ) : null}
              </ModalBody>

              <ModalFooter>
                <Button mr={3} onClick={onClose}>
                  {t('accountBilling:doNotCancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={status === 'loading'}
                  bg="rgba(255, 83, 83, 0.1)"
                  color="secondary.500"
                  _hover={{
                    bg: 'rgba(255, 83, 83, 0.2)',
                  }}
                  _active={{
                    bg: 'rgba(255, 83, 83, 0.2)',
                  }}
                >
                  {t('accountBilling:startCancellationProcess')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default AccountCancellationModal;
